
import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { fetchBlogPost, BlogFrontMatter } from "@/lib/blog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";

export default function BlogPost({ lang = "en" }: { lang?: string }) {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogFrontMatter | null>(null);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    async function fetchPost() {
      setLoading(true);
      setError(null);
      
      if (!slug) {
        console.error("No slug provided");
        setError(t("blog_post_not_found"));
        setLoading(false);
        return;
      }
      
      try {
        console.debug(`Fetching blog post with slug: ${slug}`);
        const result = await fetchBlogPost(slug, lang);
        
        if (!result) {
          console.error(`No result for blog post ${slug}`);
          setError(t("blog_post_not_found"));
          setLoading(false);
          return;
        }
        
        console.debug(`Retrieved blog post:`, result.frontMatter);
        console.debug(`Content length: ${result.content.length}`);
        
        setPost(result.frontMatter);
        setContent(result.content);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching blog post:", err);
        setError(t("failed_to_load_blog_post"));
        setLoading(false);
      }
    }
    
    fetchPost();
  }, [slug, lang, navigate, t]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    
    try {
      const date = new Date(dateString);
      
      // Check if date is valid before formatting
      if (isNaN(date.getTime())) {
        console.error("Invalid date:", dateString);
        return ""; 
      }
      
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString; // Return original string if parsing fails
    }
  };

  // FIXED: Handle cover property correctly
  const getCoverImage = () => {
    if (!post?.cover) return null;
    
    if (typeof post.cover === 'string') {
      return post.cover;
    } else if (typeof post.cover === 'object' && post.cover.image) {
      return post.cover.image;
    }
    
    return null;
  };

  const getCoverAlt = () => {
    if (!post?.cover) return '';
    
    if (typeof post.cover === 'object' && post.cover.alt) {
      return post.cover.alt;
    }
    
    return post.title;
  };

  const getCoverCaption = () => {
    if (!post?.cover) return '';
    
    if (typeof post.cover === 'object' && post.cover.caption) {
      return post.cover.caption;
    }
    
    return '';
  };

  if (loading) {
    return (
      <div className="container flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="container py-12">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-6">{t("not_found_title")}</h1>
          <p className="mb-8">{error || t("post_not_found_message")}</p>
          <Link
            to="/blog"
            className="inline-flex items-center text-primary hover:underline"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> {t("back_to_blog")}
          </Link>
        </div>
      </div>
    );
  }

  const coverImage = getCoverImage();

  return (
    <div className="container py-12">
      <Link
        to="/blog"
        className="inline-flex items-center mb-8 text-primary hover:underline"
      >
        <ArrowLeft className="mr-2 h-4 w-4" /> {t("back_to_blog")}
      </Link>
      
      <Card className="card-enhanced max-w-3xl mx-auto">
        <CardContent className="p-8">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mt-2 mb-4">{post.title}</h1>
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {post.tags.map(tag => (
                  <Badge key={`${post.slug}-${tag}`} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
            {coverImage && (
              <div className="mb-6">
                <img
                  src={coverImage}
                  alt={getCoverAlt()}
                  className="rounded-lg w-full max-h-96 object-cover"
                  style={{ objectPosition: 'center' }}
                />
                {getCoverCaption() && (
                  <div className="text-sm text-muted-foreground mt-2 text-center italic">
                    {getCoverCaption()}
                  </div>
                )}
              </div>
            )}
            <div 
              className="prose prose-lg dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
